import React from "react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

interface MapSectionProps {
  cityName?: string;
  lat: number;
  lng: number;
}

const mapContainerStyle = {
  width: "100%",
  height: "350px",
};

// Mapping city names to their descriptions
const cityDescriptions: { [key: string]: string } = {
  Bengaluru: `
    Bangalore, officially known as Bengaluru, is the capital of Karnataka and a dynamic hub in southern India. 
    Often referred to as the "Silicon Valley of India," it is a global center for technology, innovation, and startups. 
    The city is home to prestigious educational institutions like IISc and IIM Bangalore, along with lush green spaces.
  `,
  Mumbai: `
    Mumbai, formerly known as Bombay, is the financial capital of India. 
    Known for its vibrant culture, bustling markets, and iconic Bollywood film industry, Mumbai exudes energy and diversity.
  `,
  Delhi: `
   Delhi, the capital of India, is a vibrant blend of history and modernity. Known for its monuments, bustling markets, and political significance, it is home to landmarks like India Gate, Red Fort, and Qutub Minar. The city offers a mix of heritage, diverse cuisine, and a thriving business environment, making it a key hub for education, commerce, and culture. With its blending of cultures, fast-paced lifestyle, and deep-rooted history, Delhi remains an exciting and dynamic city, welcoming people from all walks of life.
  `,
  Chennai: `
    Chennai formerly known as Madras, is the capital city of Tamil Nadu, located on the southeastern coast of India. It is known for its rich cultural heritage, historic temples, beautiful beaches, and thriving IT industry. As one of India's major metropolitan cities, Chennai is a hub for education, healthcare, and business, attracting professionals and students from across the country.The city is also famous for its delicious South Indian cuisine, vibrant arts scene, and warm hospitality.
  `,
  Kolkata: `
    Kolkata, once the capital of British India, is celebrated for its literary, artistic, and revolutionary heritage. 
    The city’s cultural festivals and historic architecture tell a story of intellectual and artistic vibrancy.F
  `,
  Hyderabad: `
        Hyderabad, the capital of Telangana, is a city of contrasts, blending tradition with modernity. 
        Known for its historic sites, iconic cuisine, and booming IT sector, Hyderabad is a melting pot of cultures and communities.
    `,
  Coimbatore: `Coimbatore is a major city in Tamil Nadu, known for its textile industry, pleasant climate, and fast-growing economy. Often called the "Manchester of South India," it is a hub for manufacturing, education, and healthcare. The city is surrounded by scenic hills, waterfalls, and temples, making it a blend of urban growth and natural beauty. Coimbatore is also a gateway to popular tourist destinations like Ooty and is famous for its entrepreneurial spirit and delicious South Indian food.`,
  Pune: 'Pune, known as the "Oxford of the East," is a major educational and IT hub in India. It blends rich history with modern infrastructure, featuring landmarks like Shaniwar Wada and Aga Khan Palace. The city is famous for its pleasant climate, vibrant nightlife, and thriving automobile and manufacturing industries. Pune also boasts a strong startup ecosystem and a growing cosmopolitan culture, making it an attractive destination for students and professionals. Pune is known for its hill stations, greenery, and great quality of life, making it ideal for living and working.',
};

const MapSection: React.FC<MapSectionProps> = ({ cityName, lat, lng }) => {
  // Default to Bangalore if no cityName is provided
  const cityTitle = cityName || "Bangalore";

  // Use the corresponding description for the city; if not found, fallback to a generic description
  const cityDescription =
    cityDescriptions[cityTitle] ||
    `
    ${cityTitle} is a remarkable city with its own unique charm and attractions.
  `;

  // Center of the map based on props
  const defaultCenter = { lat, lng };

  // Define marker list (using a single marker from props, can be extended)
  const markerList = [{ lat, lng }];

  // Google Maps link for navigation
  const mapsLink = `https://maps.google.com/?q=${lat},${lng}`;

  // Map options to remove default UI controls
  const mapOptions = {
    disableDefaultUI: true, // Disables default map features
  };

  return (
    <div className="relative w-full max-w-5xl mx-auto">
      {/* Google Map with markers using @react-google-maps/api */}
      <div className="relative h-[350px] rounded-lg overflow-hidden shadow-lg">
        <LoadScript
          googleMapsApiKey="AIzaSyD7PEuJ8KF2Wd6D5aQzU6hGZ5UQ3jH15TU"
          libraries={["places"]}
          onLoad={() => console.log("Google Maps Loaded")}
          onError={() => console.log("Google Maps Failed to Load")}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={13}
            center={defaultCenter}
            options={mapOptions}
          >
            {markerList.map((marker, index) => (
              <Marker
                key={index}
                position={{ lat: marker.lat, lng: marker.lng }}
                onClick={() => window.open(mapsLink, "_blank")}
              />
            ))}
          </GoogleMap>
        </LoadScript>
      </div>

      {/* Floating Info Card */}
      <div className="relative -mt-20 mx-auto max-w-4xl bg-white p-6 rounded-lg shadow-xl">
        <h2 className="text-2xl font-bold text-gray-800">{cityTitle}</h2>
        <div className="columns-2">
          <p className="text-gray-500 text-sm leading-relaxed mt-2">
            {cityDescription}
          </p>
        </div>

        {/* Get Directions Button */}
        <a
          href={mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-4 text-red-500 font-semibold hover:underline"
        >
          Get Directions →
        </a>
      </div>
    </div>
  );
};

export default MapSection;
